// Conversacion completa de un caso en una sola linea de tiempo: los mensajes guardados del caso (mensajesCaso: el
// historial de Zammad y las respuestas enviadas desde el caso), el correo de su hilo y el de los hilos unidos (respuestas
// que llegaron en otro hilo con [Ticket#N] y casos unidos a este, D50). Antes cada cosa se veia por separado: un caso de
// Zammad mostraba solo mensajesCaso, un caso de correo solo su hilo, y los hilos unidos iban plegados al final ("Correo
// unido a este caso"); al cambiar de vista parecia que se borraban mensajes (casos #20261021620016 y #20260622620009).
import type { MensajeCaso } from '../api/casos';
import type { CorreoOculto, Mensaje } from '../api/correo';
import { correosDe, type MsgConv, nombreDeDireccion } from '../ui/Conversacion';
import { remitente } from './correo';

// Desde el 25-oct-2026 Zammad ya no lee los buzones del CRM (D81): hasta ese dia el correo del hilo propio de un caso
// de Zammad ya esta en sus mensajes guardados (lo trajo la sincronizacion) y no se repite.
export const ZAMMAD_HASTA = '2026-10-25T00:00:00Z';
// Mismo remitente y hora cercana: es el mismo mensaje. La respuesta que guarda la consola al enviar queda segundos
// despues del correo; el articulo de Zammad, hasta unos minutos despues (lo leia cada pocos minutos).
const MISMO_GUARDADO_MS = 5 * 60_000;
const MISMO_ZAMMAD_MS = 30 * 60_000;

// Un correo del buzon como mensaje de la conversacion.
export function msgDeCorreo(m: Mensaje, mios: string[]): MsgConv {
  const de = remitente(m);
  const h = (de?.handle ?? '').toLowerCase();
  const rol = (x: string) => m.participantes.filter((p) => p.role === x).map((p) => p.handle.toLowerCase());
  const mio = mios.includes(h);
  return {
    id: m.id,
    asunto: m.asunto,
    de: h,
    deNombre: mio ? `Tú (${de?.handle})` : de ? de.displayName?.trim() || de.handle : 'Desconocido',
    para: rol('TO'),
    cc: rol('CC'),
    fecha: m.receivedAt,
    texto: m.texto,
    mio,
    adjuntosDe: m.id,
    headerMessageId: m.headerMessageId,
  };
}

// Junta todo sin repetir, en orden de fecha. `correo`: los mensajes de cada hilo del caso (el propio y los unidos).
// Una copia al caso de un correo de un buzon personal (capa 3) no se repite para su dueno, que ve el original (con sus
// adjuntos): se reconoce por su Message-ID. `ocultos`: correos de los hilos del caso que quien mira no puede leer (capa
// 4): si no estan ni en el correo ni copiados, queda una linea "X respondio desde su buzon" (solo quien y cuando).
export function conversacionDeCaso(o: { origen: string; hiloId: string | null; correo: Mensaje[]; guardados: MsgConv[]; mios: string[]; ocultos?: CorreoOculto[] }): MsgConv[] {
  const enCorreo = new Set(o.correo.map((m) => m.headerMessageId).filter((x): x is string => !!x));
  const guardados = o.guardados.filter((g) => !g.origen || !enCorreo.has(g.origen));
  const vistos = new Set<string>();
  const delCorreo: MsgConv[] = [];
  for (const m of [...o.correo].sort((a, b) => a.receivedAt.localeCompare(b.receivedAt))) {
    // El mismo correo puede estar en dos hilos o en dos buzones: una sola vez (por su Message-ID).
    const clave = m.headerMessageId || m.id;
    if (vistos.has(clave)) continue;
    vistos.add(clave);
    if (o.origen === 'ZAMMAD' && m.messageThreadId === o.hiloId && m.receivedAt < ZAMMAD_HASTA) continue;
    const c = msgDeCorreo(m, o.mios);
    const t = Date.parse(c.fecha);
    const repetido = guardados.some((g) => !g.interno && !g.origen && g.de.toLowerCase() === c.de && Math.abs(Date.parse(g.fecha) - t) < (g.zammadId ? MISMO_ZAMMAD_MS : MISMO_GUARDADO_MS));
    if (!repetido) delCorreo.push(c);
  }
  const copiados = new Set(guardados.map((g) => g.origen).filter((x): x is string => !!x));
  const ocultos = (o.ocultos ?? []).filter((x) => !x.messageId || (!enCorreo.has(x.messageId) && !copiados.has(x.messageId))).map(msgDeOculto);
  return [...guardados, ...delCorreo, ...ocultos].sort((a, b) => Date.parse(a.fecha) - Date.parse(b.fecha));
}

// Un correo que no se puede leer, como linea de la conversacion: sin texto ni destinatarios.
export const msgDeOculto = (x: CorreoOculto): MsgConv => ({
  id: `oculto:${x.messageId ?? x.fecha}`,
  asunto: '',
  de: x.correo,
  deNombre: x.nombre,
  para: [],
  cc: [],
  fecha: x.fecha,
  texto: '',
  mio: true,
  oculto: true,
});

// Hilos de correo de un caso: el propio primero y luego los unidos, sin repetir.
export const hilosDeCaso = (c: { hiloId: string | null; hilosUnidos: string[] }) => [...new Set([c.hiloId, ...c.hilosUnidos].filter((h): h is string => !!h))];

// Un mensaje guardado del caso (mensajesCaso) como mensaje de la conversacion. `texto`: el de Zammad con las marcas de
// sus imagenes, si ya se trajo.
export const msgDeGuardado = (m: MensajeCaso, texto?: string): MsgConv => ({
  id: m.id,
  asunto: m.asunto,
  de: correosDe(m.de)[0] ?? '',
  deNombre: nombreDeDireccion(m.de) || (m.entrante ? 'Cliente' : 'Equipo'),
  para: correosDe(m.para),
  cc: correosDe(m.cc),
  fecha: m.fecha,
  texto: texto ?? m.texto,
  mio: !m.entrante,
  interno: m.interno,
  zammadId: m.zammadId,
  origen: m.messageIdOrigen ?? null,
  copiado: !!m.messageIdOrigen,
});
